import React, { useState } from 'react';
import axios from 'axios';
import './CreateJobModal.css';

const GARMENT_TYPES = [
  'T-Shirt',
  'Long Sleeve T-Shirt',
  'Hoodie',
  'Sweatshirt',
  'Crewneck',
  'Polo',
  'Tank Top',
  'Jersey',
  'Jacket',
  'Hat/Cap',
  'Bag',
  'Other'
];

const SIZE_COLUMNS = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

const WIZARD_STEPS = [
  { id: 1, title: 'Customer', icon: '👤' },
  { id: 2, title: 'Garments', icon: '👕' },
  { id: 3, title: 'Details', icon: '📋' },
  { id: 4, title: 'Files', icon: '🎨' },
  { id: 5, title: 'Review', icon: '✓' }
];

const createEmptySizes = () => ({
  XS: 0, S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0, '4XL': 0, '5XL': 0
});

const createEmptyColor = () => ({
  id: Date.now() + Math.random().toString(36).substr(2, 9),
  color_name: '',
  price_adjustment: '',
  sizes: createEmptySizes()
});

const createEmptyGarment = () => ({
  id: Date.now() + Math.random().toString(36).substr(2, 9),
  garment_type: '',
  base_price: '',
  size_prices: {}, // Optional price overrides per size
  colors: [createEmptyColor()]
});

const CreateJobModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [customerData, setCustomerData] = useState({
    customer_name: '',
    school_name: '',
    contact_name: '',
    contact_phone: '',
    contact_email: ''
  });

  const [garments, setGarments] = useState([createEmptyGarment()]);

  const [jobDetails, setJobDetails] = useState({
    job_description: '',
    priority: 'normal',
    due_date: '',
    total_price: ''
  });

  const [artFiles, setArtFiles] = useState([]);
  const [notes, setNotes] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState({});

  // Format phone number
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Calculate total quantity from all garments (across all colors)
  const calculateTotalQuantity = () => {
    return garments.reduce((total, garment) => {
      return total + getGarmentQuantity(garment);
    }, 0);
  };

  // Calculate quantity for a single garment (all colors combined)
  const getGarmentQuantity = (garment) => {
    return garment.colors.reduce((total, color) => {
      return total + getColorQuantity(color);
    }, 0);
  };

  // Calculate quantity for a single color
  const getColorQuantity = (color) => {
    return Object.values(color.sizes).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
  };

  // Calculate total price for the order
  const calculateTotalPrice = () => {
    let total = 0;
    garments.forEach(garment => {
      const basePrice = parseFloat(garment.base_price) || 0;
      garment.colors.forEach(color => {
        const colorAdj = parseFloat(color.price_adjustment) || 0;
        SIZE_COLUMNS.forEach(size => {
          const qty = parseInt(color.sizes[size]) || 0;
          if (qty > 0) {
            // Use size-specific price if set, otherwise base price
            const sizePrice = garment.size_prices[size] !== undefined && garment.size_prices[size] !== ''
              ? parseFloat(garment.size_prices[size])
              : basePrice;
            total += qty * (sizePrice + colorAdj);
          }
        });
      });
    });
    return total;
  };

  // Validate current step
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!customerData.customer_name.trim()) {
          newErrors.customer_name = 'Customer name is required';
        }
        break;
      case 2:
        garments.forEach((garment, gIndex) => {
          if (!garment.garment_type) {
            newErrors[`garment_${gIndex}_type`] = 'Select a garment type';
          }
          // Check each color has at least one size quantity
          garment.colors.forEach((color, cIndex) => {
            const colorQty = getColorQuantity(color);
            if (colorQty === 0) {
              newErrors[`garment_${gIndex}_color_${cIndex}_sizes`] = 'Enter at least one size quantity';
            }
          });
        });
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const goToStep = (step) => {
    if (step < currentStep || validateStep(currentStep)) {
      setCurrentStep(step);
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Customer handlers
  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'contact_phone') {
      processedValue = formatPhoneNumber(value);
    }

    setCustomerData(prev => ({ ...prev, [name]: processedValue }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Garment handlers
  const addGarment = () => {
    setGarments(prev => [...prev, createEmptyGarment()]);
  };

  const removeGarment = (garmentId) => {
    if (garments.length > 1) {
      setGarments(prev => prev.filter(g => g.id !== garmentId));
    }
  };

  const duplicateGarment = (garment) => {
    const newGarment = {
      ...createEmptyGarment(),
      garment_type: garment.garment_type,
      base_price: garment.base_price,
      size_prices: { ...garment.size_prices },
      colors: garment.colors.map(c => ({
        ...createEmptyColor(),
        color_name: '',
        price_adjustment: c.price_adjustment,
        sizes: { ...c.sizes }
      }))
    };
    setGarments(prev => [...prev, newGarment]);
  };

  const updateGarment = (garmentId, field, value) => {
    setGarments(prev => prev.map(g =>
      g.id === garmentId ? { ...g, [field]: value } : g
    ));

    // Clear related errors
    const index = garments.findIndex(g => g.id === garmentId);
    if (errors[`garment_${index}_type`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`garment_${index}_type`];
        return newErrors;
      });
    }
  };

  const updateGarmentSizePrice = (garmentId, size, value) => {
    setGarments(prev => prev.map(g =>
      g.id === garmentId
        ? { ...g, size_prices: { ...g.size_prices, [size]: value } }
        : g
    ));
  };

  // Color handlers
  const addColor = (garmentId) => {
    setGarments(prev => prev.map(g =>
      g.id === garmentId
        ? { ...g, colors: [...g.colors, createEmptyColor()] }
        : g
    ));
  };

  const removeColor = (garmentId, colorId) => {
    setGarments(prev => prev.map(g => {
      if (g.id === garmentId && g.colors.length > 1) {
        return { ...g, colors: g.colors.filter(c => c.id !== colorId) };
      }
      return g;
    }));
  };

  const updateColor = (garmentId, colorId, field, value) => {
    setGarments(prev => prev.map(g =>
      g.id === garmentId
        ? {
            ...g,
            colors: g.colors.map(c =>
              c.id === colorId ? { ...c, [field]: value } : c
            )
          }
        : g
    ));
  };

  const updateColorSize = (garmentId, colorId, size, value) => {
    const qty = Math.max(0, parseInt(value) || 0);
    setGarments(prev => prev.map(g =>
      g.id === garmentId
        ? {
            ...g,
            colors: g.colors.map(c =>
              c.id === colorId
                ? { ...c, sizes: { ...c.sizes, [size]: qty } }
                : c
            )
          }
        : g
    ));

    // Clear size errors for this color
    const gIndex = garments.findIndex(g => g.id === garmentId);
    const garment = garments[gIndex];
    if (garment) {
      const cIndex = garment.colors.findIndex(c => c.id === colorId);
      const errorKey = `garment_${gIndex}_color_${cIndex}_sizes`;
      if (errors[errorKey]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    }
  };

  // Job details handlers
  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'total_price') {
      processedValue = value === '' ? '' : parseFloat(value) || '';
    }

    setJobDetails(prev => ({ ...prev, [name]: processedValue }));
  };

  // File handlers
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = '';
  };

  const processFiles = (files) => {
    if (files.length === 0) return;

    const newFiles = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Date.now() + Math.random().toString(36).substr(2, 9)
    }));

    setArtFiles(prev => [...prev, ...newFiles]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleRemoveFile = (fileId) => {
    setArtFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    return '📎';
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      setError('Please complete all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();

      // Customer data
      Object.keys(customerData).forEach(key => {
        if (customerData[key]) {
          submitData.append(key, customerData[key]);
        }
      });

      // Calculate total quantity
      const totalQuantity = calculateTotalQuantity();
      submitData.append('quantity', totalQuantity);

      // Job details
      Object.keys(jobDetails).forEach(key => {
        if (jobDetails[key] !== '' && jobDetails[key] !== null) {
          submitData.append(key, jobDetails[key]);
        }
      });

      // Notes
      if (notes) {
        submitData.append('notes', notes);
      }

      // Garments as order_details with pricing
      const orderDetails = {
        garments: garments.map(g => ({
          garment_type: g.garment_type,
          base_price: g.base_price || null,
          size_prices: g.size_prices || {},
          colors: g.colors.map(c => ({
            color_name: c.color_name,
            price_adjustment: c.price_adjustment || null,
            sizes: c.sizes,
            quantity: getColorQuantity(c)
          })),
          quantity: getGarmentQuantity(g)
        })),
        calculated_total: calculateTotalPrice()
      };
      submitData.append('order_details', JSON.stringify(orderDetails));

      // Use first garment's type and first color for legacy fields
      if (garments.length > 0) {
        submitData.append('garment_type', garments[0].garment_type);
        if (garments[0].colors.length > 0) {
          submitData.append('garment_color', garments[0].colors[0].color_name);
        }
      }

      // Art files
      artFiles.forEach(({ file }) => {
        submitData.append('art_files', file);
      });

      const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
      await axios.post(`${baseURL}/kerrscreen/jobs`, submitData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(true);

      // Reset form
      setTimeout(() => {
        resetForm();
        onSuccess?.();
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setCustomerData({
      customer_name: '',
      school_name: '',
      contact_name: '',
      contact_phone: '',
      contact_email: ''
    });
    setGarments([createEmptyGarment()]);
    setJobDetails({
      job_description: '',
      priority: 'normal',
      due_date: '',
      total_price: ''
    });
    setArtFiles([]);
    setNotes('');
    setErrors({});
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCustomerStep();
      case 2:
        return renderGarmentsStep();
      case 3:
        return renderDetailsStep();
      case 4:
        return renderFilesStep();
      case 5:
        return renderReviewStep();
      default:
        return null;
    }
  };

  // Step 1: Customer Information
  const renderCustomerStep = () => (
    <div className="wizard-step-content">
      <div className="step-header">
        <h3>Customer Information</h3>
        <p>Enter the customer and contact details for this job.</p>
      </div>

      <div className="form-grid">
        <div className="form-group full-width">
          <label htmlFor="customer_name">
            Customer Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="customer_name"
            name="customer_name"
            value={customerData.customer_name}
            onChange={handleCustomerChange}
            placeholder="Enter customer or business name"
            className={errors.customer_name ? 'input-error' : ''}
            autoFocus
          />
          {errors.customer_name && (
            <span className="error-text">{errors.customer_name}</span>
          )}
        </div>

        <div className="form-group full-width">
          <label htmlFor="school_name">School / Organization</label>
          <input
            type="text"
            id="school_name"
            name="school_name"
            value={customerData.school_name}
            onChange={handleCustomerChange}
            placeholder="Enter school or organization name (optional)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="contact_name">Contact Name</label>
          <input
            type="text"
            id="contact_name"
            name="contact_name"
            value={customerData.contact_name}
            onChange={handleCustomerChange}
            placeholder="Primary contact person"
          />
        </div>

        <div className="form-group">
          <label htmlFor="contact_phone">Phone</label>
          <input
            type="tel"
            id="contact_phone"
            name="contact_phone"
            value={customerData.contact_phone}
            onChange={handleCustomerChange}
            placeholder="123-456-7890"
          />
        </div>

        <div className="form-group full-width">
          <label htmlFor="contact_email">Email</label>
          <input
            type="email"
            id="contact_email"
            name="contact_email"
            value={customerData.contact_email}
            onChange={handleCustomerChange}
            placeholder="email@example.com"
          />
        </div>
      </div>
    </div>
  );

  // Step 2: Garments
  const renderGarmentsStep = () => (
    <div className="wizard-step-content">
      <div className="step-header">
        <h3>Garments & Sizes</h3>
        <p>Add garment types with pricing, colors, and size quantities.</p>
      </div>

      <div className="garments-container">
        {garments.map((garment, gIndex) => (
          <div key={garment.id} className="garment-card">
            <div className="garment-card-header">
              <span className="garment-number">Garment {gIndex + 1}</span>
              <div className="garment-actions">
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => duplicateGarment(garment)}
                  title="Duplicate garment"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                </button>
                {garments.length > 1 && (
                  <button
                    type="button"
                    className="btn-icon btn-danger"
                    onClick={() => removeGarment(garment.id)}
                    title="Remove garment"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="garment-details">
              {/* Garment Type and Base Price */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Garment Type <span className="required">*</span>
                  </label>
                  <select
                    value={garment.garment_type}
                    onChange={(e) => updateGarment(garment.id, 'garment_type', e.target.value)}
                    className={errors[`garment_${gIndex}_type`] ? 'input-error' : ''}
                  >
                    <option value="">-- Select Type --</option>
                    {GARMENT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors[`garment_${gIndex}_type`] && (
                    <span className="error-text">{errors[`garment_${gIndex}_type`]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Base Price ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={garment.base_price}
                    onChange={(e) => updateGarment(garment.id, 'base_price', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Size-Specific Pricing (Collapsible) */}
              <details className="size-pricing-section">
                <summary className="size-pricing-toggle">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                  Size-Specific Pricing (optional)
                </summary>
                <div className="size-pricing-grid">
                  {SIZE_COLUMNS.map(size => (
                    <div key={size} className="size-price-cell">
                      <label className="size-label">{size}</label>
                      <div className="price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={garment.size_prices[size] || ''}
                          onChange={(e) => updateGarmentSizePrice(garment.id, size, e.target.value)}
                          placeholder={garment.base_price || '—'}
                          className="size-price-input"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="size-pricing-hint">Leave blank to use base price. Set specific prices for sizes that cost more (e.g., 2XL+).</p>
              </details>

              {/* Colors Section */}
              <div className="colors-section">
                <div className="colors-header">
                  <label>Colors & Quantities <span className="required">*</span></label>
                </div>

                {garment.colors.map((color, cIndex) => (
                  <div key={color.id} className="color-card">
                    <div className="color-card-header">
                      <span className="color-number">Color {cIndex + 1}</span>
                      {garment.colors.length > 1 && (
                        <button
                          type="button"
                          className="btn-icon-small btn-danger"
                          onClick={() => removeColor(garment.id, color.id)}
                          title="Remove color"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="color-details">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Color Name</label>
                          <input
                            type="text"
                            value={color.color_name}
                            onChange={(e) => updateColor(garment.id, color.id, 'color_name', e.target.value)}
                            placeholder="e.g., Navy Blue"
                          />
                        </div>
                        <div className="form-group">
                          <label>Price Adjustment ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={color.price_adjustment}
                            onChange={(e) => updateColor(garment.id, color.id, 'price_adjustment', e.target.value)}
                            placeholder="+0.00"
                          />
                          <span className="input-hint">Additional cost per piece for this color</span>
                        </div>
                      </div>

                      <div className="size-grid-container">
                        <div className="size-grid">
                          {SIZE_COLUMNS.map(size => (
                            <div key={size} className="size-cell">
                              <label className="size-label">{size}</label>
                              <input
                                type="number"
                                min="0"
                                value={color.sizes[size] || ''}
                                onChange={(e) => updateColorSize(garment.id, color.id, size, e.target.value)}
                                placeholder="0"
                                className="size-input"
                              />
                            </div>
                          ))}
                        </div>
                        {errors[`garment_${gIndex}_color_${cIndex}_sizes`] && (
                          <span className="error-text">{errors[`garment_${gIndex}_color_${cIndex}_sizes`]}</span>
                        )}
                        <div className="color-total">
                          {color.color_name || `Color ${cIndex + 1}`}: <strong>{getColorQuantity(color)}</strong> pieces
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn-add-color"
                  onClick={() => addColor(garment.id)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Color
                </button>
              </div>

              <div className="garment-total">
                Garment Total: <strong>{getGarmentQuantity(garment)}</strong> pieces
              </div>
            </div>
          </div>
        ))}

        <button type="button" className="btn-add-garment" onClick={addGarment}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Another Garment
        </button>
      </div>

      <div className="garments-summary">
        <div className="summary-item">
          <span className="summary-label">Total Garments:</span>
          <span className="summary-value">{garments.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Colors:</span>
          <span className="summary-value">{garments.reduce((sum, g) => sum + g.colors.length, 0)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Pieces:</span>
          <span className="summary-value">{calculateTotalQuantity()}</span>
        </div>
        {calculateTotalPrice() > 0 && (
          <div className="summary-item summary-total">
            <span className="summary-label">Estimated Total:</span>
            <span className="summary-value">${calculateTotalPrice().toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Step 3: Job Details
  const renderDetailsStep = () => (
    <div className="wizard-step-content">
      <div className="step-header">
        <h3>Job Details</h3>
        <p>Add description, priority, and scheduling information.</p>
      </div>

      <div className="form-grid">
        <div className="form-group full-width">
          <label htmlFor="job_description">Job Description</label>
          <textarea
            id="job_description"
            name="job_description"
            value={jobDetails.job_description}
            onChange={handleDetailsChange}
            placeholder="Describe the print job requirements, locations, ink colors, etc."
            rows="4"
          />
        </div>

        <div className="form-group">
          <label htmlFor="priority">Priority</label>
          <select
            id="priority"
            name="priority"
            value={jobDetails.priority}
            onChange={handleDetailsChange}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="rush">Rush</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="due_date">Due Date</label>
          <input
            type="date"
            id="due_date"
            name="due_date"
            value={jobDetails.due_date}
            onChange={handleDetailsChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="total_price">Price ($)</label>
          <input
            type="number"
            id="total_price"
            name="total_price"
            value={jobDetails.total_price}
            onChange={handleDetailsChange}
            min="0"
            step="0.01"
            placeholder="0.00"
          />
        </div>
      </div>
    </div>
  );

  // Step 4: Art Files & Notes
  const renderFilesStep = () => (
    <div className="wizard-step-content">
      <div className="step-header">
        <h3>Art Files & Notes</h3>
        <p>Upload artwork files and add any additional notes.</p>
      </div>

      <div className="form-section">
        <label>Art Files</label>
        <div
          className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="art-files-upload"
            multiple
            onChange={handleFileUpload}
            className="file-input"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.svg,.ai,.psd,.eps"
          />
          <label htmlFor="art-files-upload" className="file-upload-label">
            <div className="upload-icon">{isDragging ? '📥' : '🎨'}</div>
            <div className="upload-text">
              <span className="upload-primary">
                {isDragging ? 'Drop files here' : 'Click to upload art files'}
              </span>
              <span className="upload-secondary">
                {isDragging ? '' : 'or drag and drop'}
              </span>
            </div>
            <span className="upload-hint">
              PDF, PNG, JPG, AI, PSD, EPS (max 20MB each)
            </span>
          </label>
        </div>

        {artFiles.length > 0 && (
          <div className="files-list">
            {artFiles.map(file => (
              <div key={file.id} className="file-item">
                <span className="file-icon">{getFileIcon(file.type)}</span>
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{formatFileSize(file.size)}</span>
                </div>
                <button
                  type="button"
                  className="file-remove"
                  onClick={() => handleRemoveFile(file.id)}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-section">
        <div className="form-group full-width">
          <label htmlFor="notes">Additional Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional instructions or notes for this job..."
            rows="4"
          />
        </div>
      </div>
    </div>
  );

  // Step 5: Review
  const renderReviewStep = () => (
    <div className="wizard-step-content">
      <div className="step-header">
        <h3>Review & Submit</h3>
        <p>Review your job details before submitting.</p>
      </div>

      <div className="review-container">
        {/* Customer Section */}
        <div className="review-section">
          <div className="review-section-header">
            <h4>Customer Information</h4>
            <button type="button" className="btn-edit-section" onClick={() => setCurrentStep(1)}>
              Edit
            </button>
          </div>
          <div className="review-grid">
            <div className="review-item">
              <span className="review-label">Customer</span>
              <span className="review-value">{customerData.customer_name}</span>
            </div>
            {customerData.school_name && (
              <div className="review-item">
                <span className="review-label">School/Org</span>
                <span className="review-value">{customerData.school_name}</span>
              </div>
            )}
            {customerData.contact_name && (
              <div className="review-item">
                <span className="review-label">Contact</span>
                <span className="review-value">{customerData.contact_name}</span>
              </div>
            )}
            {customerData.contact_phone && (
              <div className="review-item">
                <span className="review-label">Phone</span>
                <span className="review-value">{customerData.contact_phone}</span>
              </div>
            )}
            {customerData.contact_email && (
              <div className="review-item">
                <span className="review-label">Email</span>
                <span className="review-value">{customerData.contact_email}</span>
              </div>
            )}
          </div>
        </div>

        {/* Garments Section */}
        <div className="review-section">
          <div className="review-section-header">
            <h4>Garments ({garments.length})</h4>
            <button type="button" className="btn-edit-section" onClick={() => setCurrentStep(2)}>
              Edit
            </button>
          </div>
          <div className="review-garments">
            {garments.map((garment, gIndex) => (
              <div key={garment.id} className="review-garment">
                <div className="review-garment-header">
                  <span className="review-garment-type">{garment.garment_type}</span>
                  <span className="review-garment-qty">{getGarmentQuantity(garment)} pcs</span>
                </div>
                {garment.base_price && (
                  <div className="review-garment-price">
                    Base: ${parseFloat(garment.base_price).toFixed(2)}/pc
                    {Object.keys(garment.size_prices).some(k => garment.size_prices[k]) && (
                      <span className="review-size-prices">
                        {' '}(Size pricing: {SIZE_COLUMNS.filter(s => garment.size_prices[s]).map(s =>
                          `${s}: $${parseFloat(garment.size_prices[s]).toFixed(2)}`
                        ).join(', ')})
                      </span>
                    )}
                  </div>
                )}
                <div className="review-colors">
                  {garment.colors.map((color, cIndex) => (
                    <div key={color.id} className="review-color">
                      <div className="review-color-header">
                        <span className="review-color-name">
                          {color.color_name || `Color ${cIndex + 1}`}
                        </span>
                        {color.price_adjustment && (
                          <span className="review-color-adj">+${parseFloat(color.price_adjustment).toFixed(2)}/pc</span>
                        )}
                        <span className="review-color-qty">{getColorQuantity(color)} pcs</span>
                      </div>
                      <div className="review-sizes">
                        {SIZE_COLUMNS.filter(size => color.sizes[size] > 0).map(size => (
                          <span key={size} className="review-size-tag">
                            {size}: {color.sizes[size]}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="review-totals">
            <div className="review-total">
              <span>Total Pieces:</span>
              <strong>{calculateTotalQuantity()}</strong>
            </div>
            {calculateTotalPrice() > 0 && (
              <div className="review-total review-price-total">
                <span>Calculated Total:</span>
                <strong>${calculateTotalPrice().toFixed(2)}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Job Details Section */}
        <div className="review-section">
          <div className="review-section-header">
            <h4>Job Details</h4>
            <button type="button" className="btn-edit-section" onClick={() => setCurrentStep(3)}>
              Edit
            </button>
          </div>
          <div className="review-grid">
            <div className="review-item">
              <span className="review-label">Priority</span>
              <span className={`review-value priority-${jobDetails.priority}`}>
                {jobDetails.priority.charAt(0).toUpperCase() + jobDetails.priority.slice(1)}
              </span>
            </div>
            {jobDetails.due_date && (
              <div className="review-item">
                <span className="review-label">Due Date</span>
                <span className="review-value">
                  {new Date(jobDetails.due_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
            {jobDetails.total_price && (
              <div className="review-item">
                <span className="review-label">Price</span>
                <span className="review-value">${parseFloat(jobDetails.total_price).toFixed(2)}</span>
              </div>
            )}
          </div>
          {jobDetails.job_description && (
            <div className="review-description">
              <span className="review-label">Description</span>
              <p>{jobDetails.job_description}</p>
            </div>
          )}
        </div>

        {/* Files & Notes Section */}
        {(artFiles.length > 0 || notes) && (
          <div className="review-section">
            <div className="review-section-header">
              <h4>Files & Notes</h4>
              <button type="button" className="btn-edit-section" onClick={() => setCurrentStep(4)}>
                Edit
              </button>
            </div>
            {artFiles.length > 0 && (
              <div className="review-files">
                <span className="review-label">Art Files ({artFiles.length})</span>
                <div className="review-file-list">
                  {artFiles.map(file => (
                    <span key={file.id} className="review-file-tag">
                      {getFileIcon(file.type)} {file.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {notes && (
              <div className="review-notes">
                <span className="review-label">Notes</span>
                <p>{notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="wizard-modal-overlay active" onClick={handleClose}>
      <div className="wizard-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="wizard-modal-header">
          <h2>Create New Job</h2>
          <button className="modal-close-btn" onClick={handleClose}>&times;</button>
        </div>

        {/* Progress Steps */}
        <div className="wizard-progress">
          {WIZARD_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`wizard-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
              onClick={() => goToStep(step.id)}
            >
              <div className="step-indicator">
                {currentStep > step.id ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <span>{step.icon}</span>
                )}
              </div>
              <span className="step-title">{step.title}</span>
              {index < WIZARD_STEPS.length - 1 && <div className="step-connector" />}
            </div>
          ))}
        </div>

        {/* Success Message */}
        {success && (
          <div className="success-message">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Job created successfully!
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Modal Body */}
        <div className="wizard-modal-body">
          {renderStepContent()}
        </div>

        {/* Modal Footer */}
        <div className="wizard-modal-footer">
          <div className="footer-left">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="btn-secondary"
                disabled={loading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Back
              </button>
            )}
          </div>
          <div className="footer-right">
            <button
              type="button"
              onClick={handleClose}
              className="btn-cancel"
              disabled={loading}
            >
              Cancel
            </button>
            {currentStep < 5 ? (
              <button
                type="button"
                onClick={nextStep}
                className="btn-primary"
                disabled={loading}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="btn-primary btn-submit"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Job'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJobModal;
